import {
  $copyNode,
  $createParagraphNode,
  $getRoot,
  $isElementNode,
  $isRootNode,
  type ElementNode,
  type LexicalNode,
} from "lexical";
import type { BlockId } from "../../core/types";
import { $getBlockId, $isIdentified } from "./state";

/**
 * Node types below which other identified nodes can be found. Tables are named by type, not by
 * class: the table nodes load on demand, and this file is part of every editor.
 */
const NESTING_TYPES: ReadonlySet<string> = new Set([
  "list",
  "listitem",
  "table",
  "tablerow",
  "tablecell",
]);

/** Containers that go when their last block goes. A cell stays: the grid depends on it. */
const REMOVED_WHEN_EMPTY: ReadonlySet<string> = new Set(["list", "listitem"]);

export const $canNestBlocks = (node: LexicalNode): node is ElementNode =>
  $isElementNode(node) && NESTING_TYPES.has(node.getType());

/** The item that exists only to hold a nested list. It belongs to the item before it. */
const $isNestedListWrapper = (node: LexicalNode | null): node is ElementNode =>
  node !== null &&
  $isElementNode(node) &&
  node.getType() === "listitem" &&
  node.getFirstChild()?.getType() === "list";

/**
 * The block at the root that holds `node`, or is it. Lexical's `getTopLevelElement` stops at a
 * shadow root, so for text in a table it answers with the paragraph in the cell; block selection,
 * handles, and reordering all mean the table.
 */
export function $rootBlockOf(node: LexicalNode): LexicalNode | null {
  let at: LexicalNode | null = node;
  for (let parent = at.getParent(); parent !== null; parent = parent.getParent()) {
    if ($isRootNode(parent)) return at;
    at = parent;
  }
  return null;
}

/** Visits every identified node in document order. One pass over blocks, never over text. */
export function $forEachBlock(visit: (node: LexicalNode, id: BlockId | null) => void): void {
  const walk = (parent: ElementNode): void => {
    for (const child of parent.getChildren()) {
      if ($isIdentified(child)) visit(child, $getBlockId(child));
      if ($canNestBlocks(child)) walk(child);
    }
  };
  walk($getRoot());
}

export type BlockLookup =
  | { ok: true; nodes: LexicalNode[] }
  | { ok: false; reason: "unknown-block" };

/**
 * Resolves IDs to nodes, in document order whatever order they were given in. Any ID that does not
 * name a block fails the whole lookup: acting on "the ones that still exist" would be guessing.
 */
export function $findBlocks(blockIds: readonly BlockId[]): BlockLookup {
  const wanted = new Set(blockIds);
  const nodes: LexicalNode[] = [];
  $forEachBlock((node, id) => {
    if (id !== null && wanted.delete(id)) nodes.push(node);
  });
  return wanted.size === 0 ? { ok: true, nodes } : { ok: false, reason: "unknown-block" };
}

/** Drops nodes that sit inside another node of the list, so nothing is handled twice. */
function outermost(nodes: readonly LexicalNode[]): LexicalNode[] {
  const keys = new Set(nodes.map((node) => node.getKey()));
  return nodes.filter((node) => !node.getParents().some((parent) => keys.has(parent.getKey())));
}

/** A list item travels with the nested list that hangs off it. */
function withNestedLists(nodes: readonly LexicalNode[]): LexicalNode[] {
  const keys = new Set(nodes.map((node) => node.getKey()));
  const expanded: LexicalNode[] = [];
  for (const node of nodes) {
    expanded.push(node);
    let next = node.getNextSibling();
    while (
      node.getType() === "listitem" &&
      $isNestedListWrapper(next) &&
      !keys.has(next.getKey())
    ) {
      expanded.push(next);
      next = next.getNextSibling();
    }
  }
  return expanded;
}

function $ensureNotEmpty(): void {
  const root = $getRoot();
  if (root.getChildrenSize() === 0) root.append($createParagraphNode()).selectStart();
}

export type MoveResult = { ok: true } | { ok: false; reason: "unknown-block" | "invalid-target" };

/**
 * Moves blocks that share a parent to another place under that same parent, keeping their order.
 * Root blocks move among root blocks and list items within their list; anything else is refused
 * rather than converted. Nodes are moved, not recreated, so IDs and content are untouched.
 */
export function $moveBlocks(blockIds: readonly BlockId[], beforeId: BlockId | null): MoveResult {
  const found = $findBlocks(blockIds);
  if (!found.ok) return found;
  const moving = withNestedLists(found.nodes);
  const first = moving[0];
  if (first === undefined) return { ok: true };
  const parent = first.getParent();
  if (parent === null || moving.some((node) => !parent.is(node.getParent()))) {
    return { ok: false, reason: "invalid-target" };
  }
  if (!$isRootNode(parent) && parent.getType() !== "list") {
    return { ok: false, reason: "invalid-target" };
  }

  const movingKeys = new Set(moving.map((node) => node.getKey()));
  let before: LexicalNode | null = null;
  if (beforeId !== null) {
    const target = $findBlocks([beforeId]);
    if (!target.ok) return target;
    before = target.nodes[0] ?? null;
    if (before === null || !parent.is(before.getParent())) {
      return { ok: false, reason: "invalid-target" };
    }
    // A nested list stays with its item: nothing may be dropped between the two.
    if ($isNestedListWrapper(before)) return { ok: false, reason: "invalid-target" };
    while (before !== null && movingKeys.has(before.getKey())) before = before.getNextSibling();
  }

  // Already there: no update, no undo step.
  const last = moving[moving.length - 1]!;
  const contiguous = moving.every(
    (node, index) => index === 0 || moving[index - 1]!.getNextSibling()?.is(node) === true,
  );
  const next = last.getNextSibling();
  if (contiguous && (before === null ? next === null : before.is(next))) return { ok: true };

  for (const node of moving) {
    if (before === null) parent.append(node);
    else before.insertBefore(node);
  }
  return { ok: true };
}

/**
 * Moves a run of adjacent siblings one place up or down, past the whole unit next to it (a list
 * item and the nested list that hangs off it count as one). At the edge it does nothing, and says
 * so by returning `moved: false`, so a caller adds no undo step.
 */
export function $moveBlocksBy(
  blockIds: readonly BlockId[],
  direction: "up" | "down",
): { ok: true; moved: boolean } | { ok: false; reason: "unknown-block" | "invalid-target" } {
  const found = $findBlocks(blockIds);
  if (!found.ok) return found;
  const moving = withNestedLists(found.nodes);
  const first = moving[0];
  const last = moving[moving.length - 1];
  if (first === undefined || last === undefined) return { ok: true, moved: false };
  const parent = first.getParent();
  const adjacent = moving.every(
    (node, index) => index === 0 || moving[index - 1]!.getNextSibling()?.is(node) === true,
  );
  if (parent === null || !adjacent) return { ok: false, reason: "invalid-target" };

  if (direction === "up") {
    // Step over the nested lists of the unit above, to land in front of the item that owns them.
    let above = first.getPreviousSibling();
    while ($isNestedListWrapper(above)) above = above.getPreviousSibling();
    if (above === null) return { ok: true, moved: false };
    for (const node of moving) above.insertBefore(node);
    return { ok: true, moved: true };
  }
  let below = last.getNextSibling();
  if (below === null) return { ok: true, moved: false };
  while ($isNestedListWrapper(below.getNextSibling())) below = below.getNextSibling()!;
  for (const node of moving.toReversed()) below.insertAfter(node);
  return { ok: true, moved: true };
}

function $cloneSubtree<T extends LexicalNode>(node: T): T {
  // `$copyNode` resets the block ID, so every copied block is identified afresh by the transform;
  // other state (a reference's target) is carried over as it is.
  const copy = $copyNode(node);
  if ($isElementNode(node) && $isElementNode(copy)) {
    copy.append(...node.getChildren().map($cloneSubtree));
  }
  return copy;
}

/** Inserts a copy of each run of adjacent blocks directly after that run. */
export function $duplicateBlocks(blockIds: readonly BlockId[]): BlockLookup {
  const found = $findBlocks(blockIds);
  if (!found.ok) return found;
  const originals = withNestedLists(outermost(found.nodes));
  const keys = new Set(originals.map((node) => node.getKey()));
  const copies: LexicalNode[] = [];
  let run: LexicalNode[] = [];
  const flush = (): void => {
    let after = run[run.length - 1];
    for (const node of run) {
      const copy = $cloneSubtree(node);
      after = after!.insertAfter(copy);
      copies.push(copy);
    }
    run = [];
  };
  for (const node of originals) {
    run.push(node);
    const next = node.getNextSibling();
    if (next === null || !keys.has(next.getKey())) flush();
  }
  return { ok: true, nodes: copies };
}

/** Removes blocks. A list left without items goes too, and the document never ends up empty. */
export function $deleteBlocks(blockIds: readonly BlockId[]): BlockLookup {
  const found = $findBlocks(blockIds);
  if (!found.ok) return found;
  const removed = withNestedLists(outermost(found.nodes));
  for (const node of removed) {
    let emptied: ElementNode | null = node.getParent();
    node.remove();
    while (emptied !== null && REMOVED_WHEN_EMPTY.has(emptied.getType()) && emptied.isEmpty()) {
      const parent: ElementNode | null = emptied.getParent();
      emptied.remove();
      emptied = parent;
    }
  }
  $ensureNotEmpty();
  return { ok: true, nodes: removed };
}
